import React, { useEffect, useState } from 'react';
import {
  Box, Typography, CircularProgress, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Button, Card, CardContent, CardHeader, useMediaQuery, Chip, IconButton, Tooltip, Tabs, Tab, TextField, MenuItem, Grid, Dialog, DialogTitle, DialogContent, DialogActions, List, ListItem, ListItemText, Alert, Snackbar, Checkbox, Toolbar, Stack, FormControlLabel, RadioGroup, Radio, Switch
} from '@mui/material';
import TablePagination from '@mui/material/TablePagination';
import { useTheme } from '@mui/material/styles';
import { Add as AddIcon, Edit as EditIcon, Visibility as ViewIcon, Download as DownloadIcon, FilterList as FilterIcon, Warning as WarningIcon, Schedule as ScheduleIcon, CheckCircle as CheckCircleIcon, ReportProblem as ReportProblemIcon, PieChart as PieChartIcon, LibraryBooks as LibraryBooksIcon, PlayCircle as PlayCircleIcon, Delete as DeleteIcon, Close as CloseIcon, Search as SearchIcon } from '@mui/icons-material';
import api from '../utils/api';
import { useDebounce } from '../hooks/useDebounce';
import { formatDateOnly } from '../utils/dateFormat';
import { departmentService } from '../services/authService';
import AssignmentForm from '../components/AssignmentForm';
import { useAuth } from '../contexts/AuthContext';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { Dayjs } from 'dayjs';

const Assignments: React.FC = () => {
  const [assignments, setAssignments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<any[]>([]);
  const [isValidating, setIsValidating] = useState(false);
  const [validationResolved, setValidationResolved] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editAssignment, setEditAssignment] = useState<any | null>(null);
  const [viewAssignment, setViewAssignment] = useState<any | null>(null);
  
  // Revision replacement state
  const [revisionReplacements, setRevisionReplacements] = useState<any[]>([]);
  const [showRevisionDialog, setShowRevisionDialog] = useState(false);
  const [pendingAssignmentData, setPendingAssignmentData] = useState<any>(null);
  
  // Success message state
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false);

  // Admin dashboard state
  const [tabValue, setTabValue] = useState(0);
  const [adminDashboardData, setAdminDashboardData] = useState<any[]>([]);
  const [allDashboardData, setAllDashboardData] = useState<any[]>([]); // Store unfiltered data
  const [dashboardLoading, setDashboardLoading] = useState(false);

  // Bulk selection
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const allSelectedOnPage = assignments.length > 0 && selectedIds.length === assignments.length;
  const toggleSelectAll = (_: any, checked: boolean) => {
    if (checked) setSelectedIds(assignments.map((a: any) => a.id));
    else setSelectedIds([]);
  };
  const toggleSelectOne = (id: number, checked: boolean) => {
    setSelectedIds(prev => checked ? Array.from(new Set([...prev, id])) : prev.filter(x => x !== id));
  };

  // Bulk action dialogs
  const [bulkDueOpen, setBulkDueOpen] = useState(false);
  const [bulkCompleteOpen, setBulkCompleteOpen] = useState(false);
  const [bulkCancelOpen, setBulkCancelOpen] = useState(false);
  const [bulkCreateOpen, setBulkCreateOpen] = useState(false);

  // Assignment Sets state
  const [sets, setSets] = useState<any[]>([]);
  const [setManagerOpen, setSetManagerOpen] = useState(false);
  const [editingSet, setEditingSet] = useState<any | null>(null);
  const [setName, setSetName] = useState('');
  const [setDescription, setSetDescription] = useState('');
  const [setItems, setSetItems] = useState<{ 
    type: 'training'|'checklist'|'document'|'log'; 
    content_type: number | null;
    content_type_str: 'training'|'checklist'|'document'|'log';
    object_id: number; 
    title: string;
    is_recurring: boolean;
    recurrence_mode: string;
    recurrence_unit: string;
    recurrence_interval: number;
    lead_time_days: number;
    fixed_weekday: number | null;
    fixed_day_of_month: number | null;
    fixed_eom: boolean;
    fixed_month: number | null;
    fixed_month_day: number | null;
  }[]>([]);
  const [addItemType, setAddItemType] = useState<'training'|'checklist'|'document'>('training');
  const [addItemOptions, setAddItemOptions] = useState<any[]>([]);
  const [pickerSelectedIds, setPickerSelectedIds] = useState<number[]>([]);

  // Assign Set dialog
  const [assignSetOpen, setAssignSetOpen] = useState(false);
  const [selectedSetId, setSelectedSetId] = useState<number | ''>('');
  const [assignTargetType, setAssignTargetType] = useState<'user'|'department'|'location'>('department');
  // Assign Set target options and selected values
  const [assignUsersOptions, setAssignUsersOptions] = useState<any[]>([]);
  const [assignDepartmentsOptions, setAssignDepartmentsOptions] = useState<any[]>([]);
  const [assignLocationsOptions, setAssignLocationsOptions] = useState<any[]>([]);
  const [selectedAssignUserIds, setSelectedAssignUserIds] = useState<number[]>([]);
  const [selectedAssignDepartmentIds, setSelectedAssignDepartmentIds] = useState<number[]>([]);
  const [selectedAssignLocationIds, setSelectedAssignLocationIds] = useState<number[]>([]);
  const [assignDueDate, setAssignDueDate] = useState<string>('');
  const [createSubscription, setCreateSubscription] = useState<boolean>(true);
  const [futureDays, setFutureDays] = useState<number>(14);

  // Bulk due form
  const [bulkDueDate, setBulkDueDate] = useState<string>('');
  const [bulkOffsetDays, setBulkOffsetDays] = useState<string>('');
  const [onlyIfEmpty, setOnlyIfEmpty] = useState<boolean>(false);

  // (separate dialogs now handle mode)

  // Bulk create by target form
  const [bulkType, setBulkType] = useState<'training'|'checklist'|'document'|'log'>('training');
  const [bulkContentId, setBulkContentId] = useState<string>('');
  const [bulkDueDateCreate, setBulkDueDateCreate] = useState<string>('');
  const [bulkCriteria, setBulkCriteria] = useState<'any'|'all'|'specific'>('all');
  const [bulkRequiredCompletions, setBulkRequiredCompletions] = useState<number>(1);
  const [bulkSendReminders, setBulkSendReminders] = useState<boolean>(true);
  const [bulkDepartments, setBulkDepartments] = useState<any[]>([]);
  const [bulkRoles, setBulkRoles] = useState<string[]>([]);
  const [bulkUsers, setBulkUsers] = useState<any[]>([]);
  const [bulkAvailableContent, setBulkAvailableContent] = useState<any[]>([]);
  const [allDepartments, setAllDepartments] = useState<any[]>([]);
  const [allUsers, setAllUsers] = useState<any[]>([]);
  const ROLE_OPTIONS = ['admin','manager','employee'];

  // Fetch set item options when set manager opens or type changes
  useEffect(() => {
    if (setManagerOpen) {
      setPickerSelectedIds([]);
      fetchSetItemOptions(addItemType);
    }
  }, [setManagerOpen, addItemType]);

  const fetchBulkContent = async () => {
    try {
      let endpoint = bulkType === 'training' ? '/trainings/' : bulkType === 'checklist' ? '/checklists/' : bulkType === 'log' ? '/log-templates/' : '/documents/';
      const resp = await api.get(endpoint);
      let content = resp.data.results || resp.data;
      if (bulkType !== 'checklist') content = content.filter((item: any) => item.is_current_version);
      setBulkAvailableContent(Array.isArray(content) ? content : []);
    } catch { setBulkAvailableContent([]); }
  };

  const fetchSetItemOptions = async (type: string) => {
    try {
      setLoading(true);
      let endpoint = '';
      switch (type) {
        case 'training':
          endpoint = '/trainings/';
          break;
        case 'checklist':
          endpoint = '/checklists/';
          break;
        case 'document':
          endpoint = '/documents/';
          break;
        case 'log':
          endpoint = '/log-templates/';
          break;
        default:
          setAddItemOptions([]);
          setLoading(false);
          return;
      }
      
      const response = await api.get(endpoint);
      let content = response.data.results || response.data;
      
      // For documents and trainings, only show current versions
      if (type === 'document' || type === 'training' || type === 'log') {
        content = content.filter((item: any) => item.is_current_version);
      }
      
      setAddItemOptions(content);
    } catch (error) {
      console.error('Failed to fetch content:', error);
      setAddItemOptions([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchSets = async () => {
    try {
      const resp = await api.get('/assignment-sets/');
      setSets(resp.data.results || resp.data || []);
    } catch (e) { console.error('fetch sets failed', e); }
  };

  const openSetManager = async () => {
    await fetchSets();
    setEditingSet(null);
    setSetName('');
    setSetDescription('');
    setSetItems([]);
    setAddItemType('training');
    setPickerSelectedIds([]);
    setSetManagerOpen(true);
  };

  const resolveSetItemType = (it: any): 'training' | 'checklist' | 'document' | 'log' => {
    const fromStr = (it.content_type_str_display || it.content_type_str || '').toLowerCase();
    if (['training', 'checklist', 'document', 'log'].includes(fromStr)) {
      return fromStr as 'training' | 'checklist' | 'document' | 'log';
    }
    const modelType = it.content_type_model === 'logtemplate' ? 'log' : (it.content_type_model || '');
    if (['training', 'checklist', 'document', 'log'].includes(modelType)) {
      return modelType as 'training' | 'checklist' | 'document' | 'log';
    }
    return 'document';
  };

  const startEditSet = (set: any) => {
    setEditingSet(set);
    setSetName(set.name || '');
    setSetDescription(set.description || '');
    setPickerSelectedIds([]);
    const enriched = set.items?.map((it: any) => {
      const type = resolveSetItemType(it);
      return ({
      type,
      content_type: it.content_type,
      content_type_str: type,
      object_id: it.object_id,
      title: it.content_title || it.title || `Unknown (ID ${it.object_id})`,
      is_recurring: it.is_recurring || false,
      recurrence_mode: it.recurrence_mode || 'last_completion',
      recurrence_unit: it.recurrence_unit || 'year',
      recurrence_interval: it.recurrence_interval || 1,
      lead_time_days: it.lead_time_days || 30,
      fixed_weekday: it.fixed_weekday || null,
      fixed_day_of_month: it.fixed_day_of_month || null,
      fixed_eom: it.fixed_eom || false,
      fixed_month: it.fixed_month || null,
      fixed_month_day: it.fixed_month_day || null,
    })}) || [];
    setSetItems(enriched);
  };

  const resetSetForm = () => {
    setEditingSet(null);
    setSetName('');
    setSetDescription('');
    setSetItems([]);
    setAddItemType('training');
    setAddItemOptions([]);
    setPickerSelectedIds([]);
  };

  const isItemInSet = (type: 'training'|'checklist'|'document'|'log', objectId: number) =>
    setItems.some(it => it.type === type && it.object_id === objectId);

  const createSetItemFromContent = (type: typeof addItemType, content: any) => ({
    type,
    content_type: null,
    content_type_str: type,
    object_id: content.id,
    title: content.title || content.name,
    is_recurring: false,
    recurrence_mode: 'last_completion',
    recurrence_unit: 'year',
    recurrence_interval: 1,
    lead_time_days: 30,
    fixed_weekday: null,
    fixed_day_of_month: null,
    fixed_eom: false,
    fixed_month: null,
    fixed_month_day: null,
  });

  const addablePickerIds = addItemOptions
    .map(c => c.id)
    .filter(id => !isItemInSet(addItemType, id));

  const allAddableSelected =
    addablePickerIds.length > 0 &&
    addablePickerIds.every(id => pickerSelectedIds.includes(id));

  const togglePickerSelectAll = () => {
    if (allAddableSelected) {
      setPickerSelectedIds(prev => prev.filter(id => !addablePickerIds.includes(id)));
    } else {
      setPickerSelectedIds(prev => Array.from(new Set([...prev, ...addablePickerIds])));
    }
  };

  const removeItemFromSetByRef = (type: 'training' | 'checklist' | 'document' | 'log', objectId: number) => {
    setSetItems(prev => prev.filter(it => !(it.type === type && it.object_id === objectId)));
  };

  const togglePickerItem = (id: number) => {
    if (isItemInSet(addItemType, id)) {
      removeItemFromSetByRef(addItemType, id);
      return;
    }
    setPickerSelectedIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const addSelectedItemsToSet = () => {
    const toAdd = addItemOptions.filter(
      c => pickerSelectedIds.includes(c.id) && !isItemInSet(addItemType, c.id)
    );
    if (toAdd.length === 0) return;
    setSetItems(prev => [
      ...prev,
      ...toAdd.map(c => createSetItemFromContent(addItemType, c)),
    ]);
    setPickerSelectedIds(prev => prev.filter(id => !toAdd.some(c => c.id === id)));
  };

  const saveSet = async () => {
    if (!setName || setItems.length === 0) return;
    
    const payload = {
      name: setName,
      description: setDescription,
      items: setItems.map(it => ({ 
        content_type_str: it.content_type_str || it.type,
        object_id: it.object_id,
        is_recurring: it.is_recurring,
        recurrence_mode: it.recurrence_mode,
        recurrence_unit: it.recurrence_unit,
        recurrence_interval: it.recurrence_interval || 1,
        lead_time_days: it.lead_time_days,
        fixed_weekday: it.fixed_weekday,
        fixed_day_of_month: it.fixed_day_of_month,
        fixed_eom: it.fixed_eom,
        fixed_month: it.fixed_month,
        fixed_month_day: it.fixed_month_day,
      })),
    };
    
    try {
      if (editingSet?.id) {
        await api.put(`/assignment-sets/${editingSet.id}/`, payload);
      } else {
        await api.post('/assignment-sets/', payload);
      }
      fetchSets();
      setEditingSet(null);
    } catch (error) {
      console.error('Failed to save set:', error);
    }
  };

  const deleteSet = async (s: any) => {
    try { await api.delete(`/assignment-sets/${s.id}/`); await fetchSets(); } catch (e) { console.error('delete set failed', e); }
  };

  const updateSetItemField = (idx: number, field: string, value: any) => {
    setSetItems(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
  };

  const removeItemFromSet = (idx: number) => {
    setSetItems(prev => prev.filter((_,i)=> i!==idx));
  };

  const openAssignSet = async () => {
    await fetchSets();
    setSelectedSetId('');
    setAssignTargetType('department');
    setAssignDueDate('');
    setCreateSubscription(true);
    setFutureDays(14);
    try {
      const [d,u,l] = await Promise.all([
        api.get('/departments/'),
        api.get('/users/assignable/'),
        api.get('/locations/'),
      ]);
      setAssignDepartmentsOptions(d.data.results || d.data || []);
      setAssignUsersOptions(u.data.results || u.data || []);
      setAssignLocationsOptions(l.data.results || l.data || []);
      // nothing selected by default
      setSelectedAssignDepartmentIds([]);
      setSelectedAssignLocationIds([]);
      setSelectedAssignUserIds([]);
    } catch {}
    setAssignSetOpen(true);
  };

  const execAssignSet = async () => {
    if (!selectedSetId || !assignDueDate) return;
    try {
      // Assign current members
      const target_ids = assignTargetType==='user' ? selectedAssignUserIds : assignTargetType==='department' ? selectedAssignDepartmentIds : selectedAssignLocationIds;
      const assignResp = await api.post(`/assignment-sets/${selectedSetId}/assign_now/`, {
        user_ids: assignTargetType==='user' ? target_ids : undefined,
        department_ids: assignTargetType==='department' ? target_ids : undefined,
        location_ids: assignTargetType==='location' ? target_ids : undefined,
        due_date: assignDueDate,
      });
      // Optional subscription for future members
      if (createSubscription) {
        await api.post(`/assignment-sets/${selectedSetId}/create_subscription/`, {
          target_type: assignTargetType,
          target_ids,
          due_date: assignDueDate,
          future_member_due_days: futureDays,
        });
      }
      setAssignSetOpen(false);
      fetchAssignments();
      setSuccessMessage(assignResp?.data?.message || 'Assignment set applied successfully.');
      setShowSuccessSnackbar(true);
    } catch (e:any) {
      const detail = e?.response?.data?.detail || e?.response?.data?.message || 'Failed to assign set.';
      setSuccessMessage(detail);
      setShowSuccessSnackbar(true);
      console.error('assign set failed', e?.response?.data || e);
    }
  };

  const fetchBulkMeta = async () => {
    try {
      const [d,u] = await Promise.all([api.get('/departments/'), api.get('/users/assignable/')]);
      setAllDepartments(d.data.results || d.data || []);
      setAllUsers(u.data.results || u.data || []);
    } catch {}
  };

  const execBulkDue = async () => {
    try {
      await api.post('/assignments/bulk_update_due_date/', {
        assignment_ids: selectedIds,
        due_date: bulkDueDate || undefined,
        offset_days: bulkOffsetDays !== '' ? parseInt(bulkOffsetDays,10) : undefined,
        only_if_empty: onlyIfEmpty,
      });
      setBulkDueOpen(false); setSelectedIds([]); await fetchAssignments();
    } catch (e) { console.error('bulk due failed', e); }
  };

  const execBulkClose = async (mode: 'cancel'|'complete') => {
    try {
      await api.post('/assignments/bulk_close/', { assignment_ids: selectedIds, mode });
      setBulkCompleteOpen(false); setBulkCancelOpen(false); setSelectedIds([]); await fetchAssignments();
    } catch (e) { console.error('bulk close failed', e); }
  };

  const execBulkCreate = async () => {
    try {
      await api.post('/assignments/bulk_create_by_target/', {
        assignment_type: bulkType,
        content_type_str: bulkType,
        object_id: parseInt(bulkContentId,10),
        due_date: bulkDueDateCreate,
        completion_criteria: bulkCriteria,
        required_completions: bulkRequiredCompletions,
        send_reminders: bulkSendReminders,
        filters: {
          department_ids: bulkDepartments.map((d:any)=>d.id),
          role: bulkRoles,
          user_ids: bulkUsers.map((u:any)=>u.id),
        },
        ignore_duplicates: false,
      });
      setBulkCreateOpen(false); await fetchAssignments();
    } catch (e) { console.error('bulk create failed', e); }
  };
  const [filters, setFilters] = useState({
    assignment_type: '',
    status: '',
    department: '',
    role: '',
    user_id: '',
    search: '',
    created_at_from: '',
    created_at_to: '',
    due_date_from: '',
    due_date_to: '',
    completed_at_from: '',
    completed_at_to: ''
  });
  const debouncedTrackingSearch = useDebounce(filters.search, 300);
  const [sortField, setSortField] = useState('last_name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [assignmentsPage, setAssignmentsPage] = useState(0);
  const [assignmentsPageSize, setAssignmentsPageSize] = useState(25);
  const [assignmentsCount, setAssignmentsCount] = useState(0);
  const [trackingPage, setTrackingPage] = useState(0);
  const [trackingPageSize, setTrackingPageSize] = useState(25);
  const [trackingCount, setTrackingCount] = useState(0);
  
  // Dropdown options
  const [departmentOptions, setDepartmentOptions] = useState<string[]>([]);
  const [roleOptions, setRoleOptions] = useState<string[]>([]);
  const [userOptions, setUserOptions] = useState<{value: string, label: string}[]>([]);

  // Reports tab state
  const [reportLoading, setReportLoading] = useState(false);
  const [reportFilters, setReportFilters] = useState<{ [key: string]: string }>({
    assignment_type: '',
    department_id: '',
    role: '',
    created_at_from: '',
    created_at_to: '',
    due_date_from: '',
    due_date_to: '',
    completed_at_from: '',
    completed_at_to: ''
  });
  const [reportMetrics, setReportMetrics] = useState<any>(null);
  const [departmentSummary, setDepartmentSummary] = useState<any[]>([]);
  const [typeBreakdown, setTypeBreakdown] = useState<any[]>([]);
  const [overdueList, setOverdueList] = useState<any[]>([]);
  const [dueSoonList, setDueSoonList] = useState<any[]>([]);
  const [reportDepartments, setReportDepartments] = useState<any[]>([]);

  const theme = useTheme();
  const { user } = useAuth();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const isAdmin = user?.role === 'admin' || user?.role === 'manager';

  const [showInactive, setShowInactive] = useState<boolean>(false);

  const [listFilters, setListFilters] = useState({
    search: '',
    assignee_id: '',
    assignment_type: '',
    status: '',
    due_date_from: '',
    due_date_to: '',
  });
  const debouncedListSearch = useDebounce(listFilters.search, 300);
  const [listFilterUsers, setListFilterUsers] = useState<any[]>([]);

  const handleListFilterChange = (field: string, value: string) => {
    setListFilters(prev => ({ ...prev, [field]: value }));
    setAssignmentsPage(0);
    setSelectedIds([]);
  };

  const clearListFilters = () => {
    setListFilters({
      search: '',
      assignee_id: '',
      assignment_type: '',
      status: '',
      due_date_from: '',
      due_date_to: '',
    });
    setAssignmentsPage(0);
    setSelectedIds([]);
  };

  const hasActiveListFilters = Boolean(
    listFilters.search
    || listFilters.assignee_id
    || listFilters.assignment_type
    || listFilters.status
    || listFilters.due_date_from
    || listFilters.due_date_to
  );

  const fetchAssignments = () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (!showInactive) params.append('is_active', 'true');
    if (debouncedListSearch) params.append('search', debouncedListSearch);
    if (listFilters.assignee_id) params.append('assignees', listFilters.assignee_id);
    if (listFilters.assignment_type) params.append('assignment_type', listFilters.assignment_type);
    if (listFilters.status) params.append('status', listFilters.status);
    if (listFilters.due_date_from) params.append('due_date_from', listFilters.due_date_from);
    if (listFilters.due_date_to) params.append('due_date_to', listFilters.due_date_to);
    params.append('page', String(assignmentsPage + 1));
    params.append('page_size', String(assignmentsPageSize));
    api.get(`/assignments/?${params.toString()}`)
      .then(response => {
        const data = response.data;
        if (data?.results) {
          setAssignments(data.results);
          setAssignmentsCount(data.count ?? data.results.length);
        } else {
          const list = Array.isArray(data) ? data : [];
          setAssignments(list);
          setAssignmentsCount(list.length);
        }
        setLoading(false);
      })
      .catch(error => {
        setError('Failed to fetch assignments');
        setLoading(false);
      });
  };

  const fetchAdminDashboardData = () => {
    setDashboardLoading(true);
    
    const params = new URLSearchParams();
    if (filters.assignment_type) params.append('assignment_type', filters.assignment_type);
    if (filters.department) params.append('department', filters.department);
    if (filters.role) params.append('role', filters.role);
    if (filters.status) params.append('status', filters.status);
    if (filters.user_id) params.append('user_id', filters.user_id);
    if (debouncedTrackingSearch) params.append('search', debouncedTrackingSearch);
    params.append('ordering', sortField);
    params.append('order', sortDirection);
    params.append('page', String(trackingPage + 1));
    params.append('page_size', String(trackingPageSize));

    api.get(`/assignments/tracking_dashboard/?${params.toString()}`)
      .then(response => {
        const data = response.data.tracking_data || [];
        setTrackingCount(response.data.count ?? data.length ?? 0);
        setAdminDashboardData(data);
        setDashboardLoading(false);
      })
      .catch((error: any) => {
        if (error?.response?.status === 403) {
          setError('You do not have permission to view Assignments Tracking.');
        } else if (error?.response?.status === 401) {
          setError('Please sign in to view this data.');
        } else {
          setError('Failed to fetch dashboard data');
        }
        setDashboardLoading(false);
      });
  };

  const fetchTrackingFilterOptions = async () => {
    try {
      const [departmentsRes, usersRes] = await Promise.all([
        api.get('/departments/'),
        api.get('/users/assignable/'),
      ]);
      const departments = (departmentsRes.data.results || departmentsRes.data || [])
        .map((dept: any) => dept.name)
        .filter(Boolean)
        .sort();
      const users = usersRes.data.results || usersRes.data || [];
      const roles = Array.from(new Set(users.map((u: any) => u.role).filter(Boolean))).sort() as string[];
      setDepartmentOptions(departments);
      setRoleOptions(roles);
      setUserOptions(
        users
          .map((u: any) => ({
            value: String(u.id),
            label: `${u.first_name} ${u.last_name}`.trim(),
          }))
          .sort((a: { label: string }, b: { label: string }) => a.label.localeCompare(b.label))
      );
    } catch {
      // Keep existing options if the lookup fails.
    }
  };

  const fetchReportsData = async () => {
    setReportLoading(true);
    try {
      // Ensure department options for id/name select
      if (reportDepartments.length === 0) {
        const depts = await departmentService.getAll();
        setReportDepartments(depts);
      }

      const params = new URLSearchParams();
      Object.entries(reportFilters).forEach(([k, v]) => { if (v) params.append(k, v); });
      // Server-side ordering/paging hints
      params.append('ordering', sortField);
      params.append('order', sortDirection);
      params.append('page', '1');
      params.append('page_size', '200');

      const [metricsRes, deptRes, typeRes, overdueRes, dueSoonRes] = await Promise.all([
        api.get(`/assignments/metrics/?${params.toString()}`),
        api.get(`/assignments/department_summary/?${params.toString()}`),
        api.get(`/assignments/type_breakdown/?${params.toString()}`),
        api.get(`/assignments/tracking_dashboard/?status=Overdue&page_size=10&${params.toString()}`),
        api.get(`/assignments/due_soon_list/?page_size=10&${params.toString()}`),
      ]);

      setReportMetrics(metricsRes.data || null);
      setDepartmentSummary(deptRes.data?.results || deptRes.data || []);
      setTypeBreakdown(typeRes.data?.results || typeRes.data || []);
      setOverdueList(overdueRes.data?.tracking_data || overdueRes.data?.results || overdueRes.data || []);
      setDueSoonList(dueSoonRes.data?.results || dueSoonRes.data || []);
    } catch (err) {
      console.error('Failed to fetch reports data', err);
      setError('Failed to fetch reports data');
    } finally {
      setReportLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      api.get('/users/assignable/')
        .then(response => setListFilterUsers(response.data.results || response.data || []))
        .catch(() => {});
    }
  }, [isAdmin]);

  useEffect(() => {
    fetchAssignments();
  }, [
    showInactive,
    assignmentsPage,
    assignmentsPageSize,
    debouncedListSearch,
    listFilters.assignee_id,
    listFilters.assignment_type,
    listFilters.status,
    listFilters.due_date_from,
    listFilters.due_date_to,
  ]);

  useEffect(() => {
    if (isAdmin && tabValue === 1) {
      fetchTrackingFilterOptions();
    }
  }, [tabValue]);

  // Reports fetch
  useEffect(() => {
    if (isAdmin && tabValue === 2) {
      fetchReportsData();
    }
  }, [tabValue]);

  useEffect(() => {
    if (isAdmin && tabValue === 2) {
      fetchReportsData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportFilters]);

  // Refetch tracking data when filters/sort change
  useEffect(() => {
    if (isAdmin && tabValue === 1) {
      fetchAdminDashboardData();
    }
  }, [
    tabValue,
    filters.assignment_type,
    filters.status,
    filters.department,
    filters.role,
    filters.user_id,
    debouncedTrackingSearch,
    sortField,
    sortDirection,
    trackingPage,
    trackingPageSize,
  ]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleFilterChange = (field: string, value: string) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setTrackingPage(0);
  };

  const handleSort = (field: string) => {
    setTrackingPage(0);
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const formatCompletionDuration = (row: any) => {
    if (row.assignment_type !== 'training') {
      return '-';
    }
    const minutes = row.completion_duration_minutes;
    if (minutes === null || minutes === undefined) {
      return '-';
    }
    if (minutes < 60) {
      return `${minutes} min`;
    }
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return remainingMinutes > 0 ? `${hours} hr ${remainingMinutes} min` : `${hours} hr`;
  };

  const handleExportCSV = async () => {
    const params = new URLSearchParams();
    if (filters.assignment_type) params.append('assignment_type', filters.assignment_type);
    if (filters.department) params.append('department', filters.department);
    if (filters.role) params.append('role', filters.role);
    if (filters.status) params.append('status', filters.status);
    if (filters.user_id) params.append('user_id', filters.user_id);
    if (debouncedTrackingSearch) params.append('search', debouncedTrackingSearch);
    params.append('ordering', sortField);
    params.append('order', sortDirection);

    try {
      const response = await api.get(`/assignments/tracking_dashboard_csv/?${params.toString()}`, {
        responseType: 'blob',
      });
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `assignments_tracking_${new Date().toISOString().slice(0, 10)}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch {
      setError('Failed to export tracking data');
    }
  };

  const getStatusChip = (status: string) => {
    const colors: { [key: string]: 'success' | 'warning' | 'error' | 'info' | 'default' } = {
      'Completed': 'success',
      'Acknowledged': 'success',
      'Pending': 'warning',
      'Overdue': 'error',
      'In Progress': 'info',
    };
    return <Chip label={status} color={colors[status] || 'default'} size="small" />;
  };

  const handleAdd = () => {
    setEditAssignment(null);
    setViewAssignment(null);
    setFormOpen(true);
  };



  const handleEdit = (assignment: any) => {
    setEditAssignment(assignment);
    setViewAssignment(null);
    setFormOpen(true);
  };

  const handleView = (assignment: any) => {
    setViewAssignment(assignment);
    setEditAssignment(null);
    setFormOpen(true);
  };

  const handleFormClose = () => {
    setFormOpen(false);
    setEditAssignment(null);
    setViewAssignment(null);
    setValidationErrors([]);
    setValidationResolved(false);
    setError(null);
  };

  // Validate assignment before submission
  const validateAssignment = async (data: any): Promise<boolean> => {
    // Clear previous validation errors
    setValidationErrors([]);
    setError(null);
    
    // Only validate documents and trainings
    if (!['document', 'training'].includes(data.assignment_type)) return true;
    
    setIsValidating(true);
    
    try {
      // For edits, include the current assignment ID to exclude it from duplicate checks
      const validationData = editAssignment 
        ? { ...data, exclude_assignment_id: editAssignment.id }
        : data;
      
      // Validate assignment data without creating it
      const response = await api.post('/assignments/validate/', validationData);
      setIsValidating(false);
      return true;
    } catch (error: any) {
      setIsValidating(false);
      
      // Handle expected validation responses
      if (error.response?.status === 400) {
        if (error.response?.data?.error === 'duplicate_assignments') {
          console.log('Setting validation errors:', error.response.data.duplicates);
          setValidationErrors(error.response.data.duplicates);
          setValidationResolved(false); // Reset resolved state when new errors are found
          setIsValidating(false);
          return false; // Block submission to show the warning and require user action
        } else if (error.response?.data?.error === 'revision_replacement_required') {
          // Handle revision replacement confirmation - this is expected, not an error
          setRevisionReplacements(error.response.data.replacements);
          setPendingAssignmentData(data);
          setShowRevisionDialog(true);
          return false;
        }
      }
      
      // For other 400 errors or non-400 errors, let the normal submission handle them
      // (The api.ts global error handler will log these)
      return true;
    }
  };

  // Handle revision replacement confirmation
  const handleRevisionReplacement = async () => {
    if (!pendingAssignmentData || revisionReplacements.length === 0) return;
    
    try {
      setLoading(true);
      
      // Call the replace_revisions endpoint
      const response = await api.post('/assignments/replace_revisions/', {
        replacements: revisionReplacements,
        assignment_data: pendingAssignmentData
      });
      
      if (response.data.success) {
        // Success! Close dialogs and refresh data
        setShowRevisionDialog(false);
        setFormOpen(false);
        setRevisionReplacements([]);
        setPendingAssignmentData(null);
        
        // Show success message
        const replacedCount = response.data.replaced_assignments?.length || 0;
        setSuccessMessage(`Successfully replaced ${replacedCount} pending assignment(s) with the new revision.`);
        setShowSuccessSnackbar(true);
        
        // Refresh assignments list
        fetchAssignments();
        if (isAdmin) {
          fetchAdminDashboardData();
        }
      }
    } catch (error: any) {
      console.error('Error replacing revisions:', error);
      setError(error.response?.data?.message || 'Failed to replace assignments with new revision');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle revision replacement cancellation
  const handleRevisionCancel = () => {
    setShowRevisionDialog(false);
    setRevisionReplacements([]);
    setPendingAssignmentData(null);
  };

  const handleFormSubmit = async (data: any) => {
    console.log('handleFormSubmit called:', { 
      editAssignment: !!editAssignment, 
      hasValidationErrors: validationErrors.length > 0, 
      validationResolved,
      data: { assignee_ids: data.assignee_ids?.length, object_id: data.object_id, assignment_type: data.assignment_type }
    });

    // If there are unresolved validation errors, don't submit
    if (validationErrors.length > 0 && !validationResolved) {
      console.log('Blocking submission: unresolved validation errors');
      return; // Stop submission if there are unresolved validation errors
    }

    // Always validate before submission, unless validation has been explicitly resolved
    if (!validationResolved) {
      console.log('Running validation before submission...');
      const isValid = await validateAssignment(data);
      if (!isValid) {
        console.log('Validation failed, stopping submission - user must resolve conflicts first');
        return; // Stop submission if validation fails
      }
      console.log('Validation passed, continuing with submission');
    } else {
      console.log('Validation already resolved by user action, skipping validation');
    }

    console.log('Proceeding with submission');

    if (editAssignment) {
      api.put(`/assignments/${editAssignment.id}/`, data)
        .then(() => {
          fetchAssignments();
          setFormOpen(false);
          setEditAssignment(null);
          setValidationErrors([]);
          setValidationResolved(false);
        })
        .catch((error) => {
          console.error('Failed to update assignment:', error.response?.data);
          setError(`Failed to update assignment: ${error.response?.data?.detail || error.message}`);
        });
    } else {
      // Since we already validated, this should succeed
      api.post('/assignments/', data)
        .then(() => {
          fetchAssignments();
          setFormOpen(false);
          setValidationErrors([]);
          setValidationResolved(false);
        })
        .catch((error) => {
          console.error('Failed to create assignment:', error.response?.data);
          setError(`Failed to create assignment: ${error.response?.data?.detail || error.message}`);
        });
    }
  };

  const getStatusColor = (status: string) => {
    const colors: { [key: string]: string } = {
      'pending': 'warning',
      'in_progress': 'info',
      'completed': 'success',
      'overdue': 'error',
      'cancelled': 'default'
    };
    return colors[status] || 'default';
  };

  const getAssignmentTypeColor = (type: string) => {
    const colors: { [key: string]: string } = {
      'training': 'primary',
      'checklist': 'secondary',
      'document': 'success',
      'log': 'warning'
    };
    return colors[type] || 'default';
  };

  const formatTypeLabel = (type?: string) => {
    if (!type) return '';
    return type.charAt(0).toUpperCase() + type.slice(1);
  };

  const formatAssignees = (assignment: any) => {
    const list = assignment?.assignees || [];
    if (!Array.isArray(list) || list.length === 0) return '-';
    const names = list.map((u: any) => {
      const first = u?.first_name || '';
      const last = u?.last_name || '';
      const name = `${first} ${last}`.trim();
      return name || u?.email || '';
    }).filter(Boolean);
    return names.length > 0 ? names.join(', ') : '-';
  };

  const renderAssigneesCell = (assignment: any) => {
    const list = assignment?.assignees || [];
    if (!Array.isArray(list) || list.length === 0) return '-';

    const names: string[] = list.map((u: any) => {
      const first = u?.first_name || '';
      const last = u?.last_name || '';
      const name = `${first} ${last}`.trim();
      return name || u?.email || '';
    }).filter(Boolean);

    if (names.length === 0) return '-';

    const displayCount = 2;
    const display = names.slice(0, displayCount).join(', ');
    const remaining = names.length - displayCount;
    const text = remaining > 0 ? `${display} +${remaining} more` : display;
    const full = names.join(', ');

    return (
      <Tooltip title={full} placement="top" arrow>
        <Box sx={{ maxWidth: 260, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{text}</Box>
      </Tooltip>
    );
  };

  const renderVersionCell = (assignment: any) => {
    const versionLabel = assignment.revision_info?.version || assignment.content_version;
    if (!versionLabel) return '-';

    const isOutdated = assignment.revision_info?.is_outdated ?? false;

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Chip
          label={versionLabel}
          color={isOutdated ? 'warning' : 'default'}
          size="small"
          variant="outlined"
          sx={{
            fontSize: '0.7rem',
            borderColor: isOutdated ? 'warning.main' : 'primary.main',
            color: isOutdated ? 'warning.main' : 'primary.main',
          }}
        />
        {isOutdated && (
          <Tooltip title="Newer version available">
            <WarningIcon color="warning" sx={{ fontSize: '1rem' }} />
          </Tooltip>
        )}
      </Box>
    );
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'No due date';
    return formatDateOnly(dateString, 'No due date');
  };

  const formatCompletion = (assignment: any) => {
    const started = assignment.started_count ?? assignment.completion_count ?? 0;
    const total = assignment.assignees?.length || 0;
    return `${started}/${total}`;
  };

  // Bulk dialogs UI
  const bulkDialogs = (
    <>
      <Dialog open={bulkDueOpen} onClose={()=>setBulkDueOpen(false)}>
        <DialogTitle>Bulk Update Due Date</DialogTitle>
        <DialogContent>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Due Date"
              value={bulkDueDate ? dayjs(bulkDueDate) : null}
              onChange={(newValue) => setBulkDueDate(newValue ? newValue.format('YYYY-MM-DD') : '')}
              slotProps={{ textField: { fullWidth: true, margin: 'dense' } }}
            />
          </LocalizationProvider>
          <TextField label="Offset Days (+/-)" fullWidth margin="dense" value={bulkOffsetDays} onChange={e=>setBulkOffsetDays(e.target.value)} />
          <FormControlLabel control={<Checkbox checked={onlyIfEmpty} onChange={e=>setOnlyIfEmpty(e.target.checked)} />} label="Only update where due date is empty" />
        </DialogContent>
        <DialogActions>
          <Button onClick={()=>setBulkDueOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={execBulkDue} disabled={selectedIds.length===0}>Apply</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={bulkCompleteOpen} onClose={()=>setBulkCompleteOpen(false)}>
        <DialogTitle>Close Selected Assignments as Completed</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This action will mark a completion record for each assignee with the current date/time and set the assignment status to Completed. Use this when work is complete outside the system.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={()=>setBulkCompleteOpen(false)}>Cancel</Button>
          <Button variant="contained" color="success" onClick={()=>execBulkClose('complete')} disabled={selectedIds.length===0}>Confirm</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={bulkCancelOpen} onClose={()=>setBulkCancelOpen(false)}>
        <DialogTitle>Cancel Selected Assignments</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This action will set the selected assignments to Cancelled. Assignees will no longer be required to complete them, and they will not count as completed.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={()=>setBulkCancelOpen(false)}>Back</Button>
          <Button variant="contained" color="warning" onClick={()=>execBulkClose('cancel')} disabled={selectedIds.length===0}>Confirm</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={bulkCreateOpen} onClose={()=>setBulkCreateOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Bulk Assign by Target</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField label="Type" select fullWidth value={bulkType} onChange={e=>{ setBulkType(e.target.value as any); setBulkContentId(''); fetchBulkContent(); fetchBulkMeta(); }}>
                <MenuItem value="training">Training</MenuItem>
                <MenuItem value="checklist">Checklist</MenuItem>
                <MenuItem value="document">Document</MenuItem>
                <MenuItem value="log">Log</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={8}>
              <TextField label="Content" select fullWidth value={bulkContentId} onChange={e=>setBulkContentId(e.target.value)}>
                {bulkAvailableContent.map((c:any)=> (
                  <MenuItem key={c.id} value={c.id}>{c.title || c.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField label="Due Date" type="date" fullWidth InputLabelProps={{ shrink:true }} value={bulkDueDateCreate} onChange={e=>setBulkDueDateCreate(e.target.value)} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField label="Criteria" select fullWidth value={bulkCriteria} onChange={e=>setBulkCriteria(e.target.value as any)}>
                <MenuItem value="any">Any</MenuItem>
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="specific">Specific</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField label="Required Completions" type="number" fullWidth value={bulkRequiredCompletions} onChange={e=>setBulkRequiredCompletions(parseInt(e.target.value,10)||1)} />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel control={<Checkbox checked={bulkSendReminders} onChange={e=>setBulkSendReminders(e.target.checked)} />} label="Send Reminders" />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField label="Departments" select fullWidth SelectProps={{ multiple:true, renderValue:(sel:any)=> (sel as unknown as number[]).map((id:number)=> (allDepartments.find((d:any)=>d.id===id)?.name || id)).join(', ') }} value={bulkDepartments.map(d=>d.id)} onChange={e=>{ const ids = (e.target.value as unknown as number[]); setBulkDepartments(allDepartments.filter((d:any)=> ids.includes(d.id))); }}>
                {allDepartments.map((d:any)=> (<MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField label="Roles" select fullWidth SelectProps={{ multiple:true, renderValue:(sel:any)=> (sel as string[]).join(', ') }} value={bulkRoles} onChange={e=>setBulkRoles(e.target.value as any)}>
                {ROLE_OPTIONS.map(r=> (<MenuItem key={r} value={r}>{r}</MenuItem>))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField label="Users" select fullWidth SelectProps={{ multiple:true, renderValue:(sel:any)=> (sel as unknown as number[]).map(id=> allUsers.find((u:any)=>u.id===id)?.email || id).join(', ') }} value={bulkUsers.map(u=>u.id)} onChange={e=>{ const ids = (e.target.value as unknown as number[]); setBulkUsers(allUsers.filter((u:any)=> ids.includes(u.id))); }}>
                {allUsers.map((u:any)=> (<MenuItem key={u.id} value={u.id}>{u.email} - {u.first_name} {u.last_name}</MenuItem>))}
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={()=>setBulkCreateOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={execBulkCreate} disabled={!bulkContentId || !bulkDueDateCreate}>Create</Button>
        </DialogActions>
      </Dialog>
    </>
  );

  if (loading && !isAdmin) return <CircularProgress />;
  if (error) return <Typography color="error">{error}</Typography>;

  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ fontSize: { xs: '1.75rem', md: '2.125rem' } }}>Assignments</Typography>
      
      {isAdmin ? (
        <Box>
          <Tabs value={tabValue} onChange={handleTabChange} sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tab label="Manage Assignments" />
          <Tab label="Assignments Tracking" />
            <Tab label="Reports" />
          </Tabs>
          
          {tabValue === 0 && (
            <Box>
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 2, alignItems: { xs: 'stretch', sm: 'center' } }}>
                <Button 
                  variant="contained" 
                  color="primary" 
                  onClick={handleAdd} 
                  startIcon={<AddIcon />}
                  sx={{ width: { xs: '100%', sm: 'auto' } }}
                >
                  Create Assignment
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={openSetManager}
                  startIcon={<LibraryBooksIcon />}
                  sx={{ width: { xs: '100%', sm: 'auto' } }}
                >
                  Manage Assignment Sets
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={openAssignSet}
                  startIcon={<PlayCircleIcon />}
                  sx={{ width: { xs: '100%', sm: 'auto' } }}
                >
                  Assign Set
                </Button>
                <FormControlLabel 
                  control={<Checkbox checked={showInactive} onChange={(e)=>{ setShowInactive(e.target.checked); setAssignmentsPage(0); }} />} 
                  label="Show Inactive"
                  sx={{ alignSelf: { xs: 'flex-start', sm: 'center' } }}
                />
              </Box>

              <Paper sx={{ p: 2, mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6">Filters</Typography>
                  {hasActiveListFilters && (
                    <Button size="small" onClick={clearListFilters}>Clear Filters</Button>
                  )}
                </Box>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Search"
                      value={listFilters.search}
                      onChange={(e) => handleListFilterChange('search', e.target.value)}
                      placeholder="Title, assignee, assigned by..."
                      InputProps={{
                        startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      size="small"
                      select
                      label="Assignee"
                      value={listFilters.assignee_id}
                      onChange={(e) => handleListFilterChange('assignee_id', e.target.value)}
                    >
                      <MenuItem value="">All Assignees</MenuItem>
                      {listFilterUsers.map((u: any) => (
                        <MenuItem key={u.id} value={String(u.id)}>
                          {u.first_name} {u.last_name} ({u.email})
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <TextField
                      fullWidth
                      size="small"
                      select
                      label="Type"
                      value={listFilters.assignment_type}
                      onChange={(e) => handleListFilterChange('assignment_type', e.target.value)}
                    >
                      <MenuItem value="">All Types</MenuItem>
                      <MenuItem value="document">Document</MenuItem>
                      <MenuItem value="training">Training</MenuItem>
                      <MenuItem value="checklist">Checklist</MenuItem>
                      <MenuItem value="log">Log</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <TextField
                      fullWidth
                      size="small"
                      select
                      label="Status"
                      value={listFilters.status}
                      onChange={(e) => handleListFilterChange('status', e.target.value)}
                    >
                      <MenuItem value="">All Statuses</MenuItem>
                      <MenuItem value="pending">Pending</MenuItem>
                      <MenuItem value="in_progress">In Progress</MenuItem>
                      <MenuItem value="completed">Completed</MenuItem>
                      <MenuItem value="overdue">Overdue</MenuItem>
                      <MenuItem value="cancelled">Cancelled</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <Typography variant="body2" color="text.secondary">
                      {assignmentsCount} assignment{assignmentsCount === 1 ? '' : 's'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        label="Due From"
                        value={listFilters.due_date_from ? dayjs(listFilters.due_date_from) : null}
                        onChange={(newVal: Dayjs | null) => handleListFilterChange('due_date_from', newVal ? newVal.format('YYYY-MM-DD') : '')}
                        slotProps={{ textField: { size: 'small', fullWidth: true } }}
                      />
                    </LocalizationProvider>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        label="Due To"
                        value={listFilters.due_date_to ? dayjs(listFilters.due_date_to) : null}
                        onChange={(newVal: Dayjs | null) => handleListFilterChange('due_date_to', newVal ? newVal.format('YYYY-MM-DD') : '')}
                        slotProps={{ textField: { size: 'small', fullWidth: true } }}
                      />
                    </LocalizationProvider>
                  </Grid>
                </Grid>
              </Paper>

              {!loading && assignments.length === 0 && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {hasActiveListFilters ? 'No assignments match your filters.' : 'No assignments found.'}
                </Alert>
              )}
              
              {isMobile ? (
        <Box>
          {assignments.map((assignment: any) => (
            <Card key={assignment.id} sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ wordBreak: 'break-word' }}>
                  {assignment.content_title || 'Unknown Content'}
                </Typography>
                <Box sx={{ mb: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  <Chip 
                    label={assignment.assignment_type?.toUpperCase() || 'N/A'} 
                    color={getAssignmentTypeColor(assignment.assignment_type) as any}
                    size="small"
                  />
                  <Chip 
                    label={assignment.status?.replace('_', ' ').toUpperCase() || 'N/A'} 
                    color={getStatusColor(assignment.status) as any}
                    size="small"
                  />
                </Box>
                {assignment.description && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1, wordBreak: 'break-word' }}>
                    {assignment.description}
                  </Typography>
                )}
                <Typography variant="body2" sx={{ mb: 0.5, wordBreak: 'break-word' }}>
                  <strong>Assigned by:</strong> {assignment.assigned_by ? `${assignment.assigned_by.first_name} ${assignment.assigned_by.last_name} (${assignment.assigned_by.email})` : 'Unknown'}
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>Assignees:</strong> {assignment.assignees?.length || 0} users
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>Completion:</strong> {formatCompletion(assignment)}
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>Due:</strong> {formatDate(assignment.due_date)}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  <strong>Created:</strong> {new Date(assignment.created_at).toLocaleDateString()}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1 }}>
                  <Button 
                    variant="outlined" 
                    size="small" 
                    onClick={() => handleView(assignment)}
                    sx={{ flex: { xs: 'none', sm: 1 }, width: { xs: '100%', sm: 'auto' } }}
                  >
                    View
                  </Button>
                  <Button 
                    variant="outlined" 
                    size="small" 
                    onClick={() => handleEdit(assignment)}
                    sx={{ flex: { xs: 'none', sm: 1 }, width: { xs: '100%', sm: 'auto' } }}
                  >
                    Edit
                  </Button>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      ) : (
        <>
        <TableContainer component={Paper} sx={{ width: '100%', overflowX: 'auto' }}>
          {selectedIds.length>0 && (
            <Toolbar sx={{ bgcolor: 'grey.100', borderBottom: 1, borderColor: 'divider' }}>
              <Stack direction="row" spacing={2} alignItems="center" sx={{ width:'100%', justifyContent:'space-between' }}>
                <Typography variant="body2">{selectedIds.length} selected</Typography>
                <Box>
                  <Button size="small" sx={{ mr:1 }} variant="outlined" onClick={()=>{ setBulkDueOpen(true); }} startIcon={<ScheduleIcon/>}>Update Due</Button>
                  <Button size="small" sx={{ mr:1 }} variant="outlined" color="success" onClick={()=>{ if(selectedIds.length>0){ setBulkCompleteOpen(true);} }} startIcon={<CheckCircleIcon/>}>Close as Completed</Button>
                  <Button size="small" variant="outlined" color="warning" onClick={()=>{ if(selectedIds.length>0){ setBulkCancelOpen(true);} }} startIcon={<ReportProblemIcon/>}>Cancel</Button>
                </Box>
              </Stack>
            </Toolbar>
          )}
          <Table size="small" sx={{ width: '100%' }}>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox color="primary" indeterminate={selectedIds.length>0 && selectedIds.length<assignments.length} checked={allSelectedOnPage} onChange={toggleSelectAll} />
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>Content</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>Type</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>Version</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>Status</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>Assigned By</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>Assignees</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>Completion</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', display: { xs: 'none', lg: 'table-cell' } }}>Due Date</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', display: { xs: 'none', xl: 'table-cell' } }}>Created</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {assignments.map((assignment: any) => (
                <TableRow key={assignment.id}>
                  <TableCell padding="checkbox">
                    <Checkbox color="primary" checked={selectedIds.includes(assignment.id)} onChange={(_, checked)=>toggleSelectOne(assignment.id, checked)} />
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {assignment.content_title || 'Unknown Content'}
                      </Typography>
                      {assignment.description && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {assignment.description.length > 50 
                            ? `${assignment.description.substring(0, 50)}...` 
                            : assignment.description}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={assignment.assignment_type?.toUpperCase() || 'N/A'} 
                      color={getAssignmentTypeColor(assignment.assignment_type) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {renderVersionCell(assignment)}
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={assignment.status?.replace('_', ' ').toUpperCase() || 'N/A'} 
                      color={getStatusColor(assignment.status) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {assignment.assigned_by ? `${assignment.assigned_by.first_name} ${assignment.assigned_by.last_name} (${assignment.assigned_by.email})` : 'Unknown'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {renderAssigneesCell(assignment)}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {formatCompletion(assignment)}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                    {formatDate(assignment.due_date)}
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', xl: 'table-cell' } }}>
                    {new Date(assignment.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Tooltip title="View Details">
                        <IconButton 
                          size="small" 
                          onClick={() => handleView(assignment)}
                          color="primary"
                        >
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit Assignment">
                        <IconButton 
                          size="small" 
                          onClick={() => handleEdit(assignment)}
                          color="secondary"
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
                </TableContainer>
        <TablePagination
                  component="div"
                  count={assignmentsCount}
                  page={assignmentsPage}
                  onPageChange={(_e, newPage) => { setAssignmentsPage(newPage); setSelectedIds([]); }}
                  rowsPerPage={assignmentsPageSize}
                  onRowsPerPageChange={(e) => { setAssignmentsPageSize(parseInt(e.target.value, 10)); setAssignmentsPage(0); setSelectedIds([]); }}
                  rowsPerPageOptions={[10, 25, 50, 100]}
                />
        {bulkDialogs}
        {/* Assignment Sets - Manager Dialog */}
        <Dialog open={setManagerOpen} onClose={()=>{ setSetManagerOpen(false); resetSetForm(); }} maxWidth="lg" fullWidth>
          <DialogTitle sx={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
            <span>{editingSet ? 'Edit Assignment Set' : 'Manage Assignment Sets'}</span>
            <IconButton onClick={()=>{ setSetManagerOpen(false); }} size="small"><CloseIcon /></IconButton>
          </DialogTitle>
          <DialogContent dividers>
            {!editingSet ? (
              <>
                <Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'center', mb:2 }}>
                  <Typography variant="subtitle1">Existing Sets</Typography>
                  <Button variant="contained" size="small" onClick={()=>{ setEditingSet({}); setSetName('New Set'); setSetDescription(''); setSetItems([]); setPickerSelectedIds([]); }}>New Set</Button>
                </Box>
                <List>
                  {sets.map((s:any)=> (
                    <ListItem key={s.id} secondaryAction={
                      <Box>
                        <IconButton onClick={()=>startEditSet(s)} size="small" color="primary"><EditIcon /></IconButton>
                        <IconButton onClick={()=>deleteSet(s)} size="small" color="error"><DeleteIcon /></IconButton>
                      </Box>
                    }>
                      <ListItemText primary={s.name} secondary={(s.items?.length || 0) + ' items'} />
                    </ListItem>
                  ))}
                  {sets.length === 0 && (
                    <ListItem><ListItemText primary="No sets yet. Click New Set to create one."/></ListItem>
                  )}
                </List>
              </>
            ) : (
              <>
                <TextField label="Set Name" fullWidth margin="dense" value={setName} onChange={e=>setSetName(e.target.value)} />
                <TextField label="Description" fullWidth margin="dense" value={setDescription} onChange={e=>setSetDescription(e.target.value)} />
                <Box sx={{ mt:2, mb:1 }}>
                  <Typography variant="subtitle2">Add Items</Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Check items to add them. Uncheck items marked &quot;In set&quot; to remove them, or use the delete icon below.
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <TextField
                    label="Type"
                    select
                    size="small"
                    sx={{ minWidth: 180 }}
                    value={addItemType}
                    onChange={e => setAddItemType(e.target.value as typeof addItemType)}
                  >
                    <MenuItem value="training">Training</MenuItem>
                    <MenuItem value="checklist">Checklist</MenuItem>
                    <MenuItem value="document">Document</MenuItem>
                  </TextField>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={addSelectedItemsToSet}
                    disabled={pickerSelectedIds.filter(id => !isItemInSet(addItemType, id)).length === 0}
                  >
                    Add Selected ({pickerSelectedIds.filter(id => !isItemInSet(addItemType, id)).length})
                  </Button>
                </Box>
                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 280, mb: 2 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox">
                          <Checkbox
                            indeterminate={
                              pickerSelectedIds.some(id => addablePickerIds.includes(id)) &&
                              !allAddableSelected
                            }
                            checked={allAddableSelected}
                            disabled={addablePickerIds.length === 0 || loading}
                            onChange={togglePickerSelectAll}
                          />
                        </TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell width={100}>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {loading ? (
                        <TableRow>
                          <TableCell colSpan={3} align="center" sx={{ py: 3 }}>
                            <CircularProgress size={24} />
                          </TableCell>
                        </TableRow>
                      ) : addItemOptions.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={3} align="center" sx={{ py: 2, color: 'text.secondary' }}>
                            No {addItemType}s available
                          </TableCell>
                        </TableRow>
                      ) : (
                        addItemOptions.map((c: any) => {
                          const inSet = isItemInSet(addItemType, c.id);
                          const checked = inSet || pickerSelectedIds.includes(c.id);
                          return (
                            <TableRow
                              key={c.id}
                              hover
                              selected={checked && !inSet}
                              sx={{ cursor: 'pointer' }}
                              onClick={() => togglePickerItem(c.id)}
                            >
                              <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                                <Checkbox
                                  checked={checked}
                                  onChange={() => togglePickerItem(c.id)}
                                />
                              </TableCell>
                              <TableCell>{c.title || c.name}</TableCell>
                              <TableCell>
                                {inSet ? (
                                  <Chip label="In set" size="small" color="success" variant="outlined" />
                                ) : null}
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Box sx={{ mb: 1 }}>
                  <Typography variant="subtitle2">Set Items ({setItems.length})</Typography>
                </Box>
                <List disablePadding>
                  {setItems.map((it, idx)=> (
                    <ListItem
                      key={`${it.content_type}-${it.object_id}-${idx}`}
                      disablePadding
                      sx={{
                        flexDirection: 'column',
                        alignItems: 'stretch',
                        py: 1,
                        px: 0,
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" fontWeight={500} noWrap>
                            {formatTypeLabel(it.type)}: {it.title}
                          </Typography>
                        </Box>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={it.is_recurring}
                              onChange={(e) => updateSetItemField(idx, 'is_recurring', e.target.checked)}
                              size="small"
                            />
                          }
                          label="Recurring"
                          sx={{ mr: 0, ml: 0 }}
                        />
                        <Tooltip title="Remove from set">
                          <IconButton size="small" onClick={() => removeItemFromSet(idx)} aria-label="Remove from set">
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>

                      {it.is_recurring && (
                        <>
                          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 0.75, alignItems: 'center' }}>
                            <TextField
                              size="small"
                              label="Mode"
                              select
                              value={it.recurrence_mode}
                              onChange={(e) => updateSetItemField(idx, 'recurrence_mode', e.target.value)}
                              sx={{ width: 150 }}
                            >
                              <MenuItem value="last_completion">Last Completion</MenuItem>
                              <MenuItem value="fixed_calendar">Fixed Calendar</MenuItem>
                            </TextField>
                            <TextField
                              size="small"
                              label="Every"
                              type="number"
                              value={it.recurrence_interval || 1}
                              onChange={(e) => updateSetItemField(idx, 'recurrence_interval', Math.max(1, parseInt(e.target.value, 10) || 1))}
                              sx={{ width: 72 }}
                              inputProps={{ min: 1 }}
                            />
                            <TextField
                              size="small"
                              label="Unit"
                              select
                              value={it.recurrence_unit}
                              onChange={(e) => updateSetItemField(idx, 'recurrence_unit', e.target.value)}
                              sx={{ width: 120 }}
                            >
                              <MenuItem value="week">Weeks</MenuItem>
                              <MenuItem value="month">Months</MenuItem>
                              <MenuItem value="quarter">Quarters</MenuItem>
                              <MenuItem value="year">Years</MenuItem>
                            </TextField>
                            <TextField
                              size="small"
                              label="Lead (days)"
                              type="number"
                              value={it.lead_time_days}
                              onChange={(e) => updateSetItemField(idx, 'lead_time_days', parseInt(e.target.value, 10) || 30)}
                              sx={{ width: 100 }}
                              inputProps={{ min: 0 }}
                            />
                          </Stack>

                          {it.recurrence_mode === 'fixed_calendar' && (
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 0.75, alignItems: 'center' }}>
                              {it.recurrence_unit === 'week' && (
                                <TextField
                                  size="small"
                                  label="Weekday"
                                  type="number"
                                  value={it.fixed_weekday ?? ''}
                                  onChange={(e) => updateSetItemField(idx, 'fixed_weekday', e.target.value === '' ? null : parseInt(e.target.value, 10))}
                                  sx={{ width: 100 }}
                                  inputProps={{ min: 0, max: 6 }}
                                  helperText="0=Mon"
                                  FormHelperTextProps={{ sx: { m: 0 } }}
                                />
                              )}
                              {it.recurrence_unit === 'month' && (
                                <>
                                  <TextField
                                    size="small"
                                    label="Day"
                                    type="number"
                                    value={it.fixed_day_of_month ?? ''}
                                    onChange={(e) => updateSetItemField(idx, 'fixed_day_of_month', e.target.value === '' ? null : parseInt(e.target.value, 10))}
                                    sx={{ width: 80 }}
                                    inputProps={{ min: 1, max: 31 }}
                                  />
                                  <FormControlLabel
                                    control={
                                      <Switch
                                        checked={it.fixed_eom}
                                        onChange={(e) => updateSetItemField(idx, 'fixed_eom', e.target.checked)}
                                        size="small"
                                      />
                                    }
                                    label="EOM"
                                  />
                                </>
                              )}
                              {it.recurrence_unit === 'year' && (
                                <>
                                  <TextField
                                    size="small"
                                    label="Month"
                                    type="number"
                                    value={it.fixed_month ?? ''}
                                    onChange={(e) => updateSetItemField(idx, 'fixed_month', e.target.value === '' ? null : parseInt(e.target.value, 10))}
                                    sx={{ width: 80 }}
                                    inputProps={{ min: 1, max: 12 }}
                                  />
                                  <TextField
                                    size="small"
                                    label="Day"
                                    type="number"
                                    value={it.fixed_month_day ?? ''}
                                    onChange={(e) => updateSetItemField(idx, 'fixed_month_day', e.target.value === '' ? null : parseInt(e.target.value, 10))}
                                    sx={{ width: 80 }}
                                    inputProps={{ min: 1, max: 31 }}
                                  />
                                </>
                              )}
                            </Stack>
                          )}
                        </>
                      )}
                    </ListItem>
                  ))}
                  {setItems.length === 0 && (
                    <ListItem><ListItemText primary="No items yet. Select items from the table above and click Add Selected."/></ListItem>
                  )}
                </List>
              </>
            )}
          </DialogContent>
          <DialogActions>
            {editingSet ? (
              <>
                <Button onClick={()=>{ setEditingSet(null); }}>Back</Button>
                <Button variant="contained" onClick={saveSet} disabled={!setName || setItems.length===0}>Save Set</Button>
              </>
            ) : (
              <Button onClick={()=> setSetManagerOpen(false)}>Close</Button>
            )}
          </DialogActions>
        </Dialog>

        {/* Assign Set Dialog */}
        <Dialog open={assignSetOpen} onClose={()=>setAssignSetOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>Assign Assignment Set</DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField label="Set" select fullWidth value={selectedSetId} onChange={e=>setSelectedSetId(parseInt(e.target.value as string,10))}>
                  <MenuItem value="">Select a set</MenuItem>
                  {sets.map((s:any)=> (<MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField label="Due Date" type="date" fullWidth InputLabelProps={{ shrink:true }} value={assignDueDate} onChange={e=>setAssignDueDate(e.target.value)} />
              </Grid>
              <Grid item xs={12}>
                <RadioGroup row value={assignTargetType} onChange={e=> setAssignTargetType(e.target.value as any)}>
                  <FormControlLabel value="department" control={<Radio />} label="Departments" />
                  <FormControlLabel value="location" control={<Radio />} label="Locations" />
                  <FormControlLabel value="user" control={<Radio />} label="Users" />
                </RadioGroup>
              </Grid>
              {assignTargetType==='department' && (
                <Grid item xs={12}>
                  <TextField label="Departments" select fullWidth SelectProps={{ multiple:true, renderValue:(sel:any)=> (sel as unknown as number[]).map((id:number)=> (assignDepartmentsOptions.find((d:any)=>d.id===id)?.name || id)).join(', ') }} value={selectedAssignDepartmentIds} onChange={e=>{ const ids = (e.target.value as unknown as number[]); setSelectedAssignDepartmentIds(ids); }}>
                    {assignDepartmentsOptions.map((d:any)=> (<MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>))}
                  </TextField>
                </Grid>
              )}
              {assignTargetType==='location' && (
                <Grid item xs={12}>
                  <TextField label="Locations" select fullWidth SelectProps={{ multiple:true, renderValue:(sel:any)=> (sel as unknown as number[]).map((id:number)=> (assignLocationsOptions.find((l:any)=>l.id===id)?.name || id)).join(', ') }} value={selectedAssignLocationIds} onChange={e=>{ const ids = (e.target.value as unknown as number[]); setSelectedAssignLocationIds(ids); }}>
                    {assignLocationsOptions.map((l:any)=> (<MenuItem key={l.id} value={l.id}>{l.name}</MenuItem>))}
                  </TextField>
                </Grid>
              )}
              {assignTargetType==='user' && (
                <Grid item xs={12}>
                  <TextField label="Users" select fullWidth SelectProps={{ multiple:true, renderValue:(sel:any)=> (sel as unknown as number[]).map((id:number)=> (assignUsersOptions.find((u:any)=>u.id===id)?.email || id)).join(', ') }} value={selectedAssignUserIds} onChange={e=>{ const ids = (e.target.value as unknown as number[]); setSelectedAssignUserIds(ids); }}>
                    {assignUsersOptions.map((u:any)=> (<MenuItem key={u.id} value={u.id}>{u.email} - {u.first_name} {u.last_name}</MenuItem>))}
                  </TextField>
                </Grid>
              )}
              {(assignTargetType === 'department' || assignTargetType === 'location') && (
                <Grid item xs={12}>
                  <FormControlLabel control={<Checkbox checked={createSubscription} onChange={(e)=> setCreateSubscription(e.target.checked)} />} label="Also create subscription for future members" />
                  {createSubscription && (
                    <TextField sx={{ ml:2, width: 160 }} label="Future Days" type="number" value={futureDays} onChange={e=> setFutureDays(parseInt(e.target.value,10)||14)} />
                  )}
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={()=> setAssignSetOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={execAssignSet} disabled={!selectedSetId || !assignDueDate}>Assign</Button>
          </DialogActions>
        </Dialog>
        </>
              )}
            </Box>
          )}
          
          {tabValue === 1 && (
            <Box>
              {/* Filters */}
              <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="h6" gutterBottom>Filters & Actions</Typography>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={6} md={2}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Search"
                      value={filters.search}
                      onChange={(e) => handleFilterChange('search', e.target.value)}
                      placeholder="Name, content, etc."
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <TextField
                      fullWidth
                      size="small"
                      select
                      label="User"
                      value={filters.user_id}
                      onChange={(e) => handleFilterChange('user_id', e.target.value)}
                    >
                      <MenuItem value="">All</MenuItem>
                      {userOptions.map((user) => (
                        <MenuItem key={user.value} value={user.value}>
                          {user.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6} md={1.5}>
                    <TextField
                      fullWidth
                      size="small"
                      select
                      label="Type"
                      value={filters.assignment_type}
                      onChange={(e) => handleFilterChange('assignment_type', e.target.value)}
                    >
                      <MenuItem value="">All</MenuItem>
                      <MenuItem value="document">Document</MenuItem>
                      <MenuItem value="training">Training</MenuItem>
                      <MenuItem value="checklist">Checklist</MenuItem>
                      <MenuItem value="log">Log</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6} md={1.5}>
                    <TextField
                      fullWidth
                      size="small"
                      select
                      label="Status"
                      value={filters.status}
                      onChange={(e) => handleFilterChange('status', e.target.value)}
                    >
                      <MenuItem value="">All</MenuItem>
                      <MenuItem value="Pending">Pending</MenuItem>
                      <MenuItem value="In Progress">In Progress</MenuItem>
                      <MenuItem value="Completed">Completed</MenuItem>
                      <MenuItem value="Overdue">Overdue</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6} md={1.5}>
                    <TextField
                      fullWidth
                      size="small"
                      select
                      label="Department"
                      value={filters.department}
                      onChange={(e) => handleFilterChange('department', e.target.value)}
                    >
                      <MenuItem value="">All</MenuItem>
                      {departmentOptions.map((dept) => (
                        <MenuItem key={dept} value={dept}>
                          {dept}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6} md={1.5}>
                    <TextField
                      fullWidth
                      size="small"
                      select
                      label="Role"
                      value={filters.role}
                      onChange={(e) => handleFilterChange('role', e.target.value)}
                    >
                      <MenuItem value="">All</MenuItem>
                      {roleOptions.map((role) => (
                        <MenuItem key={role} value={role}>
                          {role}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6} md={1.5}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleExportCSV}
                      startIcon={<DownloadIcon />}
                      fullWidth
                    >
                      Export CSV
                    </Button>
                  </Grid>
                </Grid>
              </Paper>

              {/* Manager scope notice */}
              {!isAdmin && user?.role === 'manager' && adminDashboardData.length === 0 && !dashboardLoading && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  No data is visible because no scope is configured for your manager account. Please ask an admin to assign departments, locations, or employees to your scope.
                </Alert>
              )}

              {/* Dashboard Table */}
              {dashboardLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <>
                <TableContainer component={Paper}>
                  <Table stickyHeader>
                    <TableHead sx={{ backgroundColor: (theme) => theme.palette.grey[100] }}>
                      <TableRow>
                        <TableCell sx={{ cursor: 'pointer', fontWeight: 700 }} onClick={() => handleSort('first_name')}>
                          First Name {sortField === 'first_name' && (sortDirection === 'asc' ? '↑' : '↓')}
                        </TableCell>
                        <TableCell sx={{ cursor: 'pointer', fontWeight: 700 }} onClick={() => handleSort('last_name')}>
                          Last Name {sortField === 'last_name' && (sortDirection === 'asc' ? '↑' : '↓')}
                        </TableCell>
                        <TableCell sx={{ cursor: 'pointer', fontWeight: 700 }} onClick={() => handleSort('department')}>
                          Department {sortField === 'department' && (sortDirection === 'asc' ? '↑' : '↓')}
                        </TableCell>
                        <TableCell sx={{ cursor: 'pointer', fontWeight: 700 }} onClick={() => handleSort('role')}>
                          Role {sortField === 'role' && (sortDirection === 'asc' ? '↑' : '↓')}
                        </TableCell>
                        <TableCell sx={{ cursor: 'pointer', fontWeight: 700 }} onClick={() => handleSort('content_title')}>
                          Content Title {sortField === 'content_title' && (sortDirection === 'asc' ? '↑' : '↓')}
                        </TableCell>
                        <TableCell sx={{ cursor: 'pointer', fontWeight: 700 }} onClick={() => handleSort('content_version')}>
                          Version {sortField === 'content_version' && (sortDirection === 'asc' ? '↑' : '↓')}
                        </TableCell>
                        <TableCell sx={{ cursor: 'pointer', fontWeight: 700 }} onClick={() => handleSort('assignment_type')}>
                          Type {sortField === 'assignment_type' && (sortDirection === 'asc' ? '↑' : '↓')}
                        </TableCell>
                        <TableCell sx={{ cursor: 'pointer', fontWeight: 700 }} onClick={() => handleSort('assigned_by_name')}>
                          Assigned By {sortField === 'assigned_by_name' && (sortDirection === 'asc' ? '↑' : '↓')}
                        </TableCell>
                        <TableCell sx={{ cursor: 'pointer', fontWeight: 700 }} onClick={() => handleSort('assigned_date')}>
                          Assigned Date {sortField === 'assigned_date' && (sortDirection === 'asc' ? '↑' : '↓')}
                        </TableCell>
                        <TableCell sx={{ cursor: 'pointer', fontWeight: 700 }} onClick={() => handleSort('due_date')}>
                          Due Date {sortField === 'due_date' && (sortDirection === 'asc' ? '↑' : '↓')}
                        </TableCell>
                        <TableCell sx={{ cursor: 'pointer', fontWeight: 700 }} onClick={() => handleSort('status')}>
                          Status {sortField === 'status' && (sortDirection === 'asc' ? '↑' : '↓')}
                        </TableCell>
                        <TableCell sx={{ cursor: 'pointer', fontWeight: 700 }} onClick={() => handleSort('completed_date')}>
                          Completed Date {sortField === 'completed_date' && (sortDirection === 'asc' ? '↑' : '↓')}
                        </TableCell>
                        <TableCell sx={{ cursor: 'pointer', fontWeight: 700 }} onClick={() => handleSort('completion_duration_minutes')}>
                          Completion Time {sortField === 'completion_duration_minutes' && (sortDirection === 'asc' ? '↑' : '↓')}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                       {adminDashboardData.length === 0 && error ? (
                         <TableRow>
                           <TableCell colSpan={13}>
                             <Alert severity={error.includes('permission') ? 'warning' : 'error'}>
                               {error}
                             </Alert>
                           </TableCell>
                         </TableRow>
                       ) : adminDashboardData.length === 0 ? (
                         <TableRow>
                           <TableCell colSpan={13}>
                             <Alert severity="info">No results match your filters.</Alert>
                           </TableCell>
                         </TableRow>
                       ) : adminDashboardData.map((row, index) => (
                        <TableRow key={index}>
                          <TableCell>{row.first_name}</TableCell>
                          <TableCell>{row.last_name}</TableCell>
                          <TableCell>{row.department}</TableCell>
                          <TableCell>{row.role}</TableCell>
                          <TableCell>{row.content_title}</TableCell>
                          <TableCell>
                            {renderVersionCell(row)}
                          </TableCell>
                          <TableCell>{formatTypeLabel(row.assignment_type)}</TableCell>
                          <TableCell>{row.assigned_by_name}</TableCell>
                          <TableCell>{formatDateOnly(row.assigned_date)}</TableCell>
                          <TableCell>{formatDateOnly(row.due_date)}</TableCell>
                          <TableCell>{getStatusChip(row.status)}</TableCell>
                          <TableCell>{formatDateOnly(row.completed_date)}</TableCell>
                          <TableCell>{formatCompletionDuration(row)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  count={trackingCount}
                  page={trackingPage}
                  onPageChange={(_e, newPage) => setTrackingPage(newPage)}
                  rowsPerPage={trackingPageSize}
                  onRowsPerPageChange={(e) => { setTrackingPageSize(parseInt(e.target.value, 10)); setTrackingPage(0); }}
                  rowsPerPageOptions={[10, 25, 50, 100]}
                />
                </>
              )}
            </Box>
          )}

          {tabValue === 2 && (
            <Box>
              {/* Filters */}
              <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="h6" gutterBottom>Report Filters</Typography>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={6} md={2}>
                    <TextField
                      fullWidth
                      size="small"
                      select
                      label="Assignment Type"
                      value={reportFilters.assignment_type}
                      onChange={(e) => setReportFilters(prev => ({ ...prev, assignment_type: e.target.value }))}
                    >
                      <MenuItem value="">All</MenuItem>
                      <MenuItem value="training">Training</MenuItem>
                      <MenuItem value="checklist">Checklist</MenuItem>
                      <MenuItem value="document">Document</MenuItem>
                      <MenuItem value="log">Log</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <TextField
                      fullWidth
                      size="small"
                      select
                      label="Department"
                      value={reportFilters.department_id}
                      onChange={(e) => setReportFilters(prev => ({ ...prev, department_id: e.target.value }))}
                    >
                      <MenuItem value="">All</MenuItem>
                      {Array.isArray(reportDepartments) && reportDepartments.map((d: any) => (
                        <MenuItem key={d.id} value={String(d.id)}>{d.name}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <TextField
                      fullWidth
                      size="small"
                      select
                      label="Role"
                      value={reportFilters.role}
                      onChange={(e) => setReportFilters(prev => ({ ...prev, role: e.target.value }))}
                    >
                      <MenuItem value="">All</MenuItem>
                      <MenuItem value="admin">Admin</MenuItem>
                      <MenuItem value="manager">Manager</MenuItem>
                      <MenuItem value="employee">Employee</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          size="small"
                          type="date"
                          label="Created From"
                          InputLabelProps={{ shrink: true }}
                          value={reportFilters.created_at_from}
                          onChange={(e) => setReportFilters(prev => ({ ...prev, created_at_from: e.target.value }))}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <DatePicker label="Due From" value={reportFilters.due_date_from ? dayjs(reportFilters.due_date_from as string) : null}
                          onChange={(newVal: Dayjs | null) => setReportFilters(prev => ({ ...prev, due_date_from: newVal ? newVal.format('YYYY-MM-DD') : '' }))}
                          slotProps={{ textField: { fullWidth: true, size: 'small', helperText: 'Start of day inclusive' } }} />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <DatePicker label="Completed From" value={reportFilters.completed_at_from ? dayjs(reportFilters.completed_at_from as string) : null}
                          onChange={(newVal: Dayjs | null) => setReportFilters(prev => ({ ...prev, completed_at_from: newVal ? newVal.format('YYYY-MM-DD') : '' }))}
                          slotProps={{ textField: { fullWidth: true, size: 'small', helperText: 'Start of day inclusive' } }} />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <DatePicker label="Created To" value={reportFilters.created_at_to ? dayjs(reportFilters.created_at_to as string) : null}
                          onChange={(newVal: Dayjs | null) => setReportFilters(prev => ({ ...prev, created_at_to: newVal ? newVal.format('YYYY-MM-DD') : '' }))}
                          slotProps={{ textField: { fullWidth: true, size: 'small', helperText: 'End of day inclusive' } }} />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <DatePicker label="Due To" value={reportFilters.due_date_to ? dayjs(reportFilters.due_date_to as string) : null}
                          onChange={(newVal: Dayjs | null) => setReportFilters(prev => ({ ...prev, due_date_to: newVal ? newVal.format('YYYY-MM-DD') : '' }))}
                          slotProps={{ textField: { fullWidth: true, size: 'small', helperText: 'End of day inclusive' } }} />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <DatePicker label="Completed To" value={reportFilters.completed_at_to ? dayjs(reportFilters.completed_at_to as string) : null}
                          onChange={(newVal: Dayjs | null) => setReportFilters(prev => ({ ...prev, completed_at_to: newVal ? newVal.format('YYYY-MM-DD') : '' }))}
                          slotProps={{ textField: { fullWidth: true, size: 'small', helperText: 'End of day inclusive' } }} />
                      </Grid>
                    </Grid>
                    </LocalizationProvider>
                  </Grid>
                </Grid>
              </Paper>

              {/* KPI Cards */}
              <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ borderTop: '4px solid', borderColor: 'error.main', backgroundColor: (theme) => theme.palette.error.light + '22' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="overline" color="error.main">Overdue Assignments</Typography>
                        <ReportProblemIcon color="error" />
                      </Box>
                      <Typography variant="h3" color="error.main">{reportMetrics?.overdue_assignments ?? '-'}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        One count per assignee
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ borderTop: '4px solid', borderColor: 'warning.main', backgroundColor: (theme) => theme.palette.warning.light + '22' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="overline" color="warning.main">Due In 7 Days</Typography>
                        <ScheduleIcon color="warning" />
                      </Box>
                      <Typography variant="h3" color="warning.main">{reportMetrics?.due_soon_assignments ?? '-'}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        One count per assignee
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ borderTop: '4px solid', borderColor: 'success.main', backgroundColor: (theme) => theme.palette.success.light + '22' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="overline" color="success.main">Users In Compliance</Typography>
                        <CheckCircleIcon color="success" />
                      </Box>
                      <Typography variant="h3" color="success.main">{reportMetrics?.users_in_compliance ?? '-'}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Active users with no overdue assignments
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ borderTop: '4px solid', borderColor: 'error.main', backgroundColor: (theme) => theme.palette.error.light + '22' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="overline" color="error.main">Users Out of Compliance</Typography>
                        <WarningIcon color="error" />
                      </Box>
                      <Typography variant="h3" color="error.main">{reportMetrics?.users_out_of_compliance ?? '-'}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Users with at least one overdue assignment
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Department Summary and Type Breakdown */}
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardHeader
                      title="Department Summary"
                      subheader="Includes a No Department row for users without a department"
                      action={<PieChartIcon color="primary" />}
                    />
                    <CardContent sx={{ pt: 0 }}>
                      <TableContainer component={Paper}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600 }}>Department</TableCell>
                              <TableCell align="right">Users In</TableCell>
                              <TableCell align="right">Users Out</TableCell>
                              <TableCell align="right">Total Active</TableCell>
                              <TableCell align="right">Overdue (Per User)</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {departmentSummary.map((row: any) => (
                              <TableRow key={row.department_id ?? 'no-department'}>
                                <TableCell>{row.department_name}</TableCell>
                                <TableCell align="right">{row.users_in_compliance}</TableCell>
                                <TableCell align="right">
                                  <Chip size="small" color={row.users_out_of_compliance > 0 ? 'error' : 'default'} label={row.users_out_of_compliance} />
                                </TableCell>
                                <TableCell align="right">{row.total_active_users}</TableCell>
                                <TableCell align="right">{row.overdue_assignments}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Card>
                    <CardHeader
                      title="Assignment Type Breakdown"
                      subheader="Counts are per assignee, not per assignment record"
                    />
                    <CardContent sx={{ pt: 0 }}>
                      <TableContainer component={Paper}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                              <TableCell align="right">Total</TableCell>
                              <TableCell align="right">Pending</TableCell>
                              <TableCell align="right">In Progress</TableCell>
                              <TableCell align="right">Completed</TableCell>
                              <TableCell align="right">Overdue (Per User)</TableCell>
                              <TableCell align="right">Cancelled</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {typeBreakdown.map((row: any) => (
                              <TableRow key={row.assignment_type}>
                                <TableCell>{row.assignment_type}</TableCell>
                                <TableCell align="right">{row.total}</TableCell>
                                <TableCell align="right">{row.pending}</TableCell>
                                <TableCell align="right">{row.in_progress}</TableCell>
                                <TableCell align="right">{row.completed}</TableCell>
                                <TableCell align="right">
                                  <Chip size="small" color={row.overdue > 0 ? 'error' : 'default'} label={row.overdue} />
                                </TableCell>
                                <TableCell align="right">{row.cancelled}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Drill-down lists */}
              <Grid container spacing={3} sx={{ mt: 1 }}>
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardHeader
                      title="Overdue (Top 10)"
                      subheader="One row per assignee"
                    />
                    <CardContent sx={{ pt: 0 }}>
                      <TableContainer component={Paper}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600 }}>Assignee</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Content</TableCell>
                              <TableCell>Type</TableCell>
                              <TableCell>Department</TableCell>
                              <TableCell>Due Date</TableCell>
                              <TableCell>Status</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {overdueList.map((row: any, index: number) => (
                              <TableRow key={`${row.assignment_id}-${row.first_name}-${row.last_name}-${index}`}>
                                <TableCell>{`${row.first_name || ''} ${row.last_name || ''}`.trim() || '-'}</TableCell>
                                <TableCell>{row.content_title}</TableCell>
                                <TableCell>{formatTypeLabel(row.assignment_type)}</TableCell>
                                <TableCell>{row.department || 'No Department'}</TableCell>
                                <TableCell>{formatDateOnly(row.due_date)}</TableCell>
                                <TableCell>
                                  <Chip label={row.status?.toUpperCase()} size="small" color={getStatusColor(row.status?.toLowerCase().replace(' ', '_')) as any} variant="filled" />
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardHeader title="Due Soon (Top 10)" />
                    <CardContent sx={{ pt: 0 }}>
                      <TableContainer component={Paper}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600 }}>Content</TableCell>
                              <TableCell>Type</TableCell>
                              <TableCell>Assignees</TableCell>
                              <TableCell>Due Date</TableCell>
                              <TableCell>Status</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {dueSoonList.map((a: any) => (
                              <TableRow key={a.id}>
                                <TableCell>{a.content_title}</TableCell>
                                <TableCell>{formatTypeLabel(a.assignment_type)}</TableCell>
                                <TableCell>{renderAssigneesCell(a)}</TableCell>
                                <TableCell>{formatDateOnly(a.due_date)}</TableCell>
                                <TableCell>
                                  <Chip label={a.status?.replace('_',' ').toUpperCase()} size="small" color={getStatusColor(a.status) as any} variant="outlined" />
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          )}
        </Box>
      ) : (
        <Box>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 2 }}>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={handleAdd} 
              startIcon={<AddIcon />}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              Create Assignment
            </Button>
          </Box>
          
          {isMobile ? (
            <Box>
              {assignments.map((assignment: any) => (
                <Card key={assignment.id} sx={{ mb: 2 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ wordBreak: 'break-word' }}>
                      {assignment.content_title || 'Unknown Content'}
                    </Typography>
                    <Box sx={{ mb: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      <Chip 
                        label={assignment.assignment_type?.toUpperCase() || 'N/A'} 
                        color={getAssignmentTypeColor(assignment.assignment_type) as any}
                        size="small"
                      />
                      <Chip 
                        label={assignment.status?.replace('_', ' ').toUpperCase() || 'N/A'} 
                        color={getStatusColor(assignment.status) as any}
                        size="small"
                      />
                    </Box>
                    {assignment.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1, wordBreak: 'break-word' }}>
                        {assignment.description}
                      </Typography>
                    )}
                    <Typography variant="body2" sx={{ mb: 0.5, wordBreak: 'break-word' }}>
                      <strong>Assigned by:</strong> {assignment.assigned_by ? `${assignment.assigned_by.first_name} ${assignment.assigned_by.last_name} (${assignment.assigned_by.email})` : 'Unknown'}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <strong>Assignees:</strong> {assignment.assignees?.length || 0} users
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <strong>Completion:</strong> {formatCompletion(assignment)}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <strong>Due:</strong> {formatDate(assignment.due_date)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      <strong>Created:</strong> {new Date(assignment.created_at).toLocaleDateString()}
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1 }}>
                      <Button 
                        variant="outlined" 
                        size="small" 
                        onClick={() => handleView(assignment)}
                        sx={{ flex: { xs: 'none', sm: 1 }, width: { xs: '100%', sm: 'auto' } }}
                      >
                        View
                      </Button>
                      <Button 
                        variant="outlined" 
                        size="small" 
                        onClick={() => handleEdit(assignment)}
                        sx={{ flex: { xs: 'none', sm: 1 }, width: { xs: '100%', sm: 'auto' } }}
                      >
                        Edit
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          ) : (
            <TableContainer component={Paper} sx={{ width: '100%', overflowX: 'auto' }}>
              <Table size="small" sx={{ width: '100%' }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>Content</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>Type</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>Status</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>Assigned By</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>Assignees</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>Completion</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap', display: { xs: 'none', lg: 'table-cell' } }}>Due Date</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap', display: { xs: 'none', xl: 'table-cell' } }}>Created</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {assignments.map((assignment: any) => (
                    <TableRow key={assignment.id}>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {assignment.content_title || 'Unknown Content'}
                          </Typography>
                          {assignment.description && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              {assignment.description.length > 50 
                                ? `${assignment.description.substring(0, 50)}...` 
                                : assignment.description}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={assignment.assignment_type?.toUpperCase() || 'N/A'} 
                          color={getAssignmentTypeColor(assignment.assignment_type) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={assignment.status?.replace('_', ' ').toUpperCase() || 'N/A'} 
                          color={getStatusColor(assignment.status) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {assignment.assigned_by ? `${assignment.assigned_by.first_name} ${assignment.assigned_by.last_name} (${assignment.assigned_by.email})` : 'Unknown'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {assignment.assignees?.length || 0} users
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {formatCompletion(assignment)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                        {formatDate(assignment.due_date)}
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', xl: 'table-cell' } }}>
                        {new Date(assignment.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Tooltip title="View Details">
                            <IconButton 
                              size="small" 
                              onClick={() => handleView(assignment)}
                              color="primary"
                            >
                              <ViewIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit Assignment">
                            <IconButton 
                              size="small" 
                              onClick={() => handleEdit(assignment)}
                              color="secondary"
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}
      
      <AssignmentForm
        open={formOpen}
        onClose={handleFormClose}
        onSubmit={handleFormSubmit}
        initialData={editAssignment}
        viewMode={!!viewAssignment}
        viewData={viewAssignment}
        validationErrors={validationErrors}
        isValidating={isValidating}
        onFormChange={() => setValidationErrors([])}
        onValidationResolved={() => {
          setValidationErrors([]);
          setValidationResolved(true);
        }}
      />
      
      {/* Revision Replacement Dialog */}
      <Dialog
        open={showRevisionDialog}
        onClose={handleRevisionCancel}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {revisionReplacements.length > 0 && revisionReplacements[0].old_document_title 
            ? 'Document Revision Replacement Required'
            : 'Training Revision Replacement Required'
          }
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              Previous Revision Assignments Detected
            </Typography>
            <Typography variant="body2">
              The following users have pending assignments for previous revisions of this {revisionReplacements.length > 0 && revisionReplacements[0].old_document_title ? 'document' : 'training'}. 
              These assignments will be automatically removed and replaced with the new revision assignment.
            </Typography>
          </Alert>
          
          <Typography variant="body1" sx={{ mb: 2, fontWeight: 'bold' }}>
            Assignments to be replaced:
          </Typography>
          
          <List>
            {revisionReplacements.map((replacement, index) => (
              <ListItem key={index} sx={{ pl: 0 }}>
                <ListItemText
                  primary={`${replacement.user_name}`}
                  secondary={
                    <Box component="div">
                      <Typography variant="body2" color="text.secondary" component="div">
                        <strong>Current:</strong> {replacement.old_document_title || replacement.old_training_title} ({replacement.old_document_version || replacement.old_training_version})
                      </Typography>
                      <Typography variant="body2" color="text.secondary" component="div">
                        <strong>New:</strong> {replacement.new_document_title || replacement.new_training_title} ({replacement.new_document_version || replacement.new_training_version})
                      </Typography>
                      <Typography variant="body2" color="text.secondary" component="div">
                        <strong>Status:</strong> {replacement.assignment_status}
                      </Typography>
                    </Box>
                  }
                />
              </ListItem>
            ))}
          </List>
          
          <Typography variant="body2" sx={{ mt: 2, fontStyle: 'italic' }}>
            Do you want to proceed with replacing these assignments?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRevisionCancel} color="inherit">
            Cancel
          </Button>
          <Button 
            onClick={handleRevisionReplacement} 
            variant="contained" 
            color="primary"
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : 'Replace Assignments'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Success Snackbar */}
      <Snackbar
        open={showSuccessSnackbar}
        autoHideDuration={6000}
        onClose={() => setShowSuccessSnackbar(false)}
        message={successMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      />
    </Box>
  );
};

export default Assignments; 